import React, { useEffect, useRef, useState } from 'react';

/**
 * WaveformVisualizer — bar visualizer fed by the recorder's `peak` value.
 * Keeps a small ring buffer of past peaks so the bars look responsive
 * but smooth. Pure CSS bars; no canvas, no external lib.
 */
interface Props {
    peak: number;
    barCount?: number;
    /** Compact mode for inline use inside the PTT button. */
    compact?: boolean;
}

const WaveformVisualizer: React.FC<Props> = ({ peak, barCount = 24, compact = false }) => {
    const ringRef = useRef<number[]>([]);
    const [bars, setBars] = useState<number[]>(() => Array(barCount).fill(0));

    useEffect(() => {
        const ring = ringRef.current;
        ring.push(peak);
        if (ring.length > barCount) ring.shift();
        setBars([...ring, ...Array(barCount - ring.length).fill(0)]);
    }, [peak, barCount]);

    return (
        <div
            className={`flex items-center justify-center ${compact ? 'gap-[2px] h-6' : 'gap-[3px] h-10'}`}
            aria-hidden="true"
        >
            {bars.map((amp, i) => {
                const h = Math.max(0.1, Math.min(1, amp * 4));
                return (
                    <div
                        key={i}
                        className="rounded-full bg-white/90"
                        style={{
                            width: compact ? 2 : 3,
                            height: `${h * 100}%`,
                            transition: 'height 60ms linear',
                        }}
                    />
                );
            })}
        </div>
    );
};

export default WaveformVisualizer;
